import { View, ImageBackground, Pressable, StyleSheet } from "react-native";
import React from "react";
import { Text } from "@ui-kitten/components";
import { CmsPlan } from "lib/sanity.types";
import { useAppSelector } from "hooks/useAppSelector";
import { getRecurringPrice, getSinglePrice, planIdProperty } from "../helpers";
import { formatCurrency } from "utils/appUtils";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import { setSelectedCmsPlan } from "features/slices/shopPagePhaseTwoSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { PortableText } from "@portabletext/react";
import Spacer from "components/Spacer";

interface Props {
  plan: CmsPlan;
  onPlanPress: (plan: CmsPlan) => void;
  isPlanSelected: boolean;
}
const PlanContainer = (props: Props) => {
  const { plan, onPlanPress, isPlanSelected } = props;
  const dispatch = useAppDispatch();
  const { signupCharges } = useAppSelector((state) => state.shopPagePhaseTwo);
  const { selectedBillingInterval } = useAppSelector((state) => state.buyFlow);
  const { data: branding } = useFetchTenantBrandingQuery();

  const getPlanPrice = (plan: CmsPlan) => {
    const planId = plan[planIdProperty];
    const signupChargeDetails = signupCharges.find(
      (charge) =>
        charge.planPackageId === planId &&
        charge.billingInterval === selectedBillingInterval
    );
    if (!signupChargeDetails) return "N/A";
    const { signupCharge } = signupChargeDetails;
    return formatCurrency(
      selectedBillingInterval === "MONTHLY"
        ? getRecurringPrice(signupCharge)
        : getSinglePrice(signupCharge)
    );
  };

  const getPlanPriceSuffix = (plan: CmsPlan) => {
    return selectedBillingInterval === "MONTHLY"
      ? plan.monthlyPriceSuffix
      : plan.payInFullPriceSuffix;
  };

  return (
    <ImageBackground
      source={
        plan.showBackgroundLogo
          ? {
              uri: `https://branding.fanrally.com/logos/${branding?.name}_dark.svg`,
            }
          : undefined
      }
      resizeMode="contain"
      imageStyle={{
        opacity: 0.3,
        left: "50%",
        transform: [{ rotate: "45deg" }, { scale: 1.5 }],
      }}
      style={{
        backgroundColor: branding?.primaryColor,
        overflow: "hidden",
      }}
    >
      <Pressable
        className="w-full p-8 md:min-w-[400px] border-[8px] rounded-md"
        onPress={() => {
          dispatch(setSelectedCmsPlan(plan));
          // Need this for NewMemberForm, to find the validation rules for this plan
          onPlanPress(plan);
        }}
        style={{
          borderColor: branding?.primaryColor,
        }}
      >
        <View style={[styles.triangle, styles.triangleLeft]} />
        <View style={[styles.triangle, styles.triangleRight]} />
        <View>
          <div className="text-white text-3xl font-semibold">{plan.name}</div>
          {isPlanSelected && (
            <div style={{ color: branding?.secondaryColor }}>
              <PortableText value={plan.description} />
            </div>
          )}
          <Spacer />
          <div
            className="self-end space-x-1"
            style={{
              color: plan.priceColor
                ? plan.priceColor.hex
                : branding?.secondaryColor,
            }}
          >
            <span className="text-xl md:text-3xl font-bold">
              {getPlanPrice(plan)}
            </span>
            <span className="text-xs md:text-sm">
              {getPlanPriceSuffix(plan)}
            </span>
          </div>
          <Text
            category="h5"
            status="control"
            style={{
              textAlign: "right",
              letterSpacing: 1.5,
            }}
          ></Text>
        </View>
      </Pressable>
    </ImageBackground>
  );
};
const TRIANGLE_SIZE = 25;
const styles = StyleSheet.create({
  triangle: {
    width: 0,
    height: 0,
    backgroundColor: "transparent",
    borderStyle: "solid",
    borderLeftWidth: TRIANGLE_SIZE,
    borderRightWidth: TRIANGLE_SIZE,
    borderBottomWidth: 20,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: "#f5f5f5",
    position: "absolute",
  },
  triangleLeft: {
    transform: [{ rotate: "90deg" }],
    top: "50%",
    left: -TRIANGLE_SIZE,
  },
  triangleRight: {
    transform: [{ rotate: "-90deg" }],
    top: "50%",
    right: -TRIANGLE_SIZE,
  },
});
export default PlanContainer;
